import React, { useState } from 'react';
import styled from 'styled-components';
import { initializeSettings } from '../../utils/initializeSettings';
import AdminNav from './AdminNav';
import { FaCog, FaCheck } from 'react-icons/fa';

const Container = styled.div`
  max-width: 1200px;
  margin: 2rem auto;
  padding: 1.5rem;
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: #333;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 1.1rem;
`;

const Card = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  margin-bottom: 2rem;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  background-color: #C41E3A;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #A01930;
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
  
  svg {
    margin-right: 0.5rem;
  }
`;

const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  color: #0f5132;
  background-color: #d1e7dd;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
  
  svg {
    margin-right: 0.5rem;
    color: #0f5132;
  }
`;

const ErrorMessage = styled.div`
  color: #842029;
  background-color: #f8d7da;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
`;

const InitializeSettingsPage = () => {
  const [initializing, setInitializing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  const handleInitializeSettings = async () => {
    setInitializing(true);
    setSuccess(false);
    setError(null);
    
    try {
      const result = await initializeSettings();
      if (result) {
        setSuccess(true);
      } else {
        setError('Failed to initialize settings. Please try again.');
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setInitializing(false);
    }
  };
  
  return (
    <Container>
      <Header>
        <Title>Initialize Store Settings</Title>
        <Subtitle>Create default store settings in Firestore</Subtitle>
      </Header>
      
      <AdminNav />
      
      <Card>
        <h2>Store Settings Initialization</h2>
        <p>
          This utility will create the default store settings document in Firestore.
          This is required for the shipping label functionality to work properly.
        </p>
        <p>
          <strong>Note:</strong> If you already have store settings configured, this will overwrite them with default values.
        </p>
        
        <Button 
          onClick={handleInitializeSettings} 
          disabled={initializing || success}
        >
          <FaCog />
          {initializing ? 'Initializing...' : 'Initialize Settings'}
        </Button>
        
        {success && (
          <SuccessMessage>
            <FaCheck />
            Settings initialized successfully! You can now configure them in the Settings page.
          </SuccessMessage>
        )}
        
        {error && (
          <ErrorMessage>
            {error}
          </ErrorMessage>
        )}
      </Card>
    </Container>
  );
};

export default InitializeSettingsPage;
